/**
 * Copyright (C) 2024 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import React, { useEffect, useState } from 'react';
import { ErrorMessage, useFormikContext } from 'formik';
import { Box } from '@mui/material';
import { shallowEqual, useSelector, useDispatch } from 'react-redux';
import { requestAntennas } from '../../../store/antennas/actions';
import {
  selectAntennas,
  selectMissionPlanById,
  selectSelectedTerminal,
  selectOverrideRecommendedAntenna,
} from '../../../store';
import { setSelectedAntenna } from '../../../store/missionPlans/actions';
import { ClassNameMap } from '@mui/styles';
import { FormikFieldEnum, FormValues } from './MissionDetailsForm';
import SelectBox from './SelectBox';
import Antenna from '../../../shared/entities/antenna';
import { FilterOptionsState } from '@mui/material/useAutocomplete';

export interface AntennaSelectFieldProps {
  styling: ClassNameMap<'label' | 'input' | 'error'>;
}

const AntennaSelectField = (props: AntennaSelectFieldProps) => {
  const { styling } = props;
  const [disable, setDisable] = useState<boolean>(true);
  const [antennaMapping, setAntennaMapping] = useState<{ [key: string]: number }>({});
  const [antennaNames, setAntennaNames] = useState<string[]>([]);
  const { values, setFieldValue } = useFormikContext<FormValues>();
  const overrideRecommended = useSelector(selectOverrideRecommendedAntenna, shallowEqual);
  const antennas: { [key: number]: Antenna } | undefined = useSelector(selectAntennas, shallowEqual);
  const selectedTerminal = useSelector(selectSelectedTerminal, shallowEqual);
  const missionPlanToEdit = useSelector(selectMissionPlanById, shallowEqual);
  const dispatch = useDispatch();

  // Request antennas when component mounts
  useEffect(() => {
    if (antennas === undefined) {
      dispatch(requestAntennas());
    }
  }, [dispatch, antennas]);

  // Sets disable state to false when the tail number isn't specified
  useEffect(() => {
    setDisable(values.tailNumber === '' ? true : false);
  }, [values.tailNumber, setDisable]);

  // Save the selected antenna in redux store when user selects an antenna
  useEffect(() => {
    if (antennas !== undefined) dispatch(setSelectedAntenna(antennas[antennaMapping[values.antennaType]]));
  }, [values.antennaType, dispatch, antennas, antennaMapping]);

  /*
   * Creates a string array of all the antenna display names
   * Creates a mapping between Antenna Display Name and Antenna Id
   * Changes antenna dropdown value to recommended antenna when user selects a tail number
   * Populates antenna field on form mount when editting a plan
   */
  useEffect(() => {
    const antennaDisplayNames: string[] = [];
    const compiledAntennaMapping: { [key: string]: number } = {};
    let autopopulated = false;
    if (antennas !== undefined) {
      Object.keys(antennas).forEach((key) => {
        // Get antenna from the given key
        const antenna = antennas[key];

        // Add displayName for the antenna to the array
        if (selectedTerminal && selectedTerminal.antennaTypeId && antenna.id === selectedTerminal.antennaTypeId) {
          antennaDisplayNames.push(antenna.displayName + ' (recommended)');

          // Add mapping from displayName to id (to search for a specific antenna quickly from the store)
          compiledAntennaMapping[antenna.displayName + ' (recommended)'] = antenna.id;

          // Overwrites the antenna name if a 'non-recommended' antenna should be autopopulated in the form
          setFieldValue(
            FormikFieldEnum.ANTENNA_TYPE,
            missionPlanToEdit.antennaTypeId !== antenna.id &&
              missionPlanToEdit.datapathUuid === selectedTerminal.datapathUuid &&
              overrideRecommended
              ? antennas[missionPlanToEdit.antennaTypeId].displayName
              : `${antenna.displayName} (recommended)`,
            false,
          );
          autopopulated = true;
        } else {
          antennaDisplayNames.push(antenna.displayName);

          // Add mapping from displayName to id (to search for a specific antenna quickly from the store)
          compiledAntennaMapping[antenna.displayName] = antenna.id;
        }
      });
      // Set the selected antenna to be the mission plan's current antenna if an
      // antenna hasn't already been autoselected for the form
      if (
        missionPlanToEdit &&
        missionPlanToEdit.antennaTypeId &&
        antennas[missionPlanToEdit.antennaTypeId]?.displayName &&
        !autopopulated
      ) {
        setFieldValue(FormikFieldEnum.ANTENNA_TYPE, antennas[missionPlanToEdit.antennaTypeId].displayName, false);
      }
    }
    // Save mapping in state
    setAntennaMapping(compiledAntennaMapping);

    // Save array of antenna display names
    antennaDisplayNames.push(''); // Prevents console warning when SelectBox has empty value ''
    setAntennaNames(antennaDisplayNames);

    // Removed setFieldValue in dependency to prevent useEffect from unnecessarily running an extra time
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [antennas, selectedTerminal]);

  // Custom filter fcn to decide which options to display based on input
  const filter = (options: any[], state: FilterOptionsState<any>) => {
    return options.filter((option) => option.toLowerCase().includes(state.inputValue.toLowerCase()));
  };

  return (
    <React.Fragment>
      <Box flexBasis="48%">
        <label htmlFor={FormikFieldEnum.ANTENNA_TYPE} className={styling.label}>
          Antenna Type*
        </label>
        <SelectBox options={antennaNames} fieldName={FormikFieldEnum.ANTENNA_TYPE} isDisabled={disable} filter={filter}>
          Select Antenna
        </SelectBox>
        <ErrorMessage component="div" name={FormikFieldEnum.ANTENNA_TYPE} className={styling.error} />
      </Box>
    </React.Fragment>
  );
};

export default AntennaSelectField;
